import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { FORMULA_VARIABLES } from '../../data/formulas';
import { CategoryFilter, CATEGORIES } from './types';
import { InlineLatex } from './LatexRenderer';

interface FormulaVariablePickerProps {
  selectedKnowns: string[];
  selectedTarget: string;
  onToggleKnown: (id: string) => void;
  onSetTarget: (id: string) => void;
}

export const FormulaVariablePicker: React.FC<FormulaVariablePickerProps> = ({
  selectedKnowns,
  selectedTarget,
  onToggleKnown,
  onSetTarget,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('TODAS');
  const [searchFilter, setSearchFilter] = useState('');
  const [hideSelectedInPicker, setHideSelectedInPicker] = useState(true);

  const displayedVariables = useMemo(() => {
    return FORMULA_VARIABLES.filter((v) => {
      if (hideSelectedInPicker && (selectedKnowns.includes(v.id) || selectedTarget === v.id)) {
        return false;
      }
      const matchCat = selectedCategory === 'TODAS' || v.category === selectedCategory;
      const matchSearch =
        !searchFilter ||
        v.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        v.symbol.toLowerCase().includes(searchFilter.toLowerCase()) ||
        v.unit.toLowerCase().includes(searchFilter.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [selectedCategory, searchFilter, hideSelectedInPicker, selectedKnowns, selectedTarget]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Category filter tabs & search affordance */}
      <div className="shrink-0 space-y-2 pt-2">
        <div className="flex items-center justify-between gap-2">
          {/* Horizontal scrollable category pills */}
          <div className="flex gap-1 overflow-x-auto no-scrollbar py-0.5 max-w-[65%] sm:max-w-[70%]">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium transition ${
                  selectedCategory === cat
                    ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 font-bold'
                    : 'bg-paper-200 text-zinc-700 hover:bg-paper-300 dark:bg-study-card dark:text-zinc-300 dark:hover:bg-study-line'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Compact search input */}
          <div className="relative flex-1 min-w-[110px]">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-zinc-400" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full rounded-md border border-paper-300 bg-paper-100 py-1 pl-7 pr-2 text-xs text-zinc-800 placeholder-zinc-400 focus:border-indigo-500 focus:outline-none dark:border-study-line dark:bg-study-card dark:text-zinc-200"
            />
          </div>
        </div>

        {/* Footprint shrink toggle */}
        <div className="flex items-center justify-between text-[11px] text-zinc-500 dark:text-zinc-400 px-1">
          <span>
            Disponíveis para escolha: <strong>{displayedVariables.length}</strong>
          </span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={hideSelectedInPicker}
              onChange={(e) => setHideSelectedInPicker(e.target.checked)}
              className="rounded border-paper-300 text-indigo-600 focus:ring-0 h-3 w-3"
            />
            <span>Recolher selecionados</span>
          </label>
        </div>
      </div>

      {/* Scrollable Grandezas Picker Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 pr-1 pt-1 mt-2">
        {displayedVariables.map((v) => {
          const isKnown = selectedKnowns.includes(v.id);
          const isTarget = selectedTarget === v.id;

          return (
            <div
              key={v.id}
              className={`flex items-center justify-between rounded-lg border p-2 transition ${
                isKnown
                  ? 'border-indigo-500 bg-indigo-50/70 text-indigo-950 dark:border-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-200'
                  : isTarget
                  ? 'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-200'
                  : 'border-paper-300 bg-paper-100 hover:border-indigo-300 hover:bg-paper-200/60 dark:border-study-line dark:bg-study-card dark:hover:border-indigo-500'
              }`}
            >
              <div className="flex items-center gap-2 overflow-hidden mr-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-paper-200 font-mono text-xs font-bold text-indigo-700 dark:bg-study-slate dark:text-indigo-300">
                  <InlineLatex>{v.symbol}</InlineLatex>
                </span>
                <div className="truncate">
                  <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100 truncate block">
                    {v.name}
                  </span>
                  <span className="text-[10px] text-zinc-400 dark:text-zinc-500">
                    {v.unit} • {v.category}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => onToggleKnown(v.id)}
                  className={`rounded px-2 py-1 text-[11px] font-semibold transition ${
                    isKnown
                      ? 'bg-indigo-600 text-white font-bold'
                      : 'bg-paper-200 text-zinc-700 hover:bg-indigo-100 hover:text-indigo-900 dark:bg-study-panel dark:text-zinc-300 dark:hover:bg-indigo-950'
                  }`}
                >
                  {isKnown ? 'Dado ✓' : '+ Dado'}
                </button>

                <button
                  type="button"
                  onClick={() => onSetTarget(v.id)}
                  className={`rounded px-2 py-1 text-[11px] font-semibold transition ${
                    isTarget
                      ? 'bg-amber-500 text-white font-bold'
                      : 'bg-paper-200 text-zinc-700 hover:bg-amber-100 hover:text-amber-900 dark:bg-study-panel dark:text-zinc-300 dark:hover:bg-amber-950'
                  }`}
                >
                  {isTarget ? 'Alvo 🎯' : '🎯 Alvo'}
                </button>
              </div>
            </div>
          );
        })}

        {displayedVariables.length === 0 && (
          <div className="py-8 text-center text-xs text-zinc-400 dark:text-zinc-500">
            Nenhuma grandeza encontrada com os filtros atuais.
          </div>
        )}
      </div>
    </div>
  );
};
